import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { CostSummaryCustomProps, SummaryItem } from '@components';
import { useAmounts } from '@hooks';
import {
	useHasPromotionalCode,
	useNavigationStore,
	usePromotionalCodeDuration,
	useSelectedPlan,
	useSelectedSolution,
} from '@store';
import { SolutionsType } from '@types';

export interface CostSummaryHelper {
	cost: CostSummaryCustomProps;
}

export const useCostSummaryHelper = (): CostSummaryHelper => {
	const t = useTranslation();
	const { flow } = useNavigationStore();
	const selectedPlan = useSelectedPlan();
	const { amounts } = useAmounts({});
	const promotionalCodeDuration = usePromotionalCodeDuration(selectedPlan);
	const hasPromotionalCode = useHasPromotionalCode();
	const solutionSelected = useSelectedSolution();
	if (flow === 'subscription') {
		const planName = t('placeholders.costSummary.title', {
			planName: selectedPlan?.name !== undefined ? selectedPlan?.name : '',
		});

		const summaryList: SummaryItem[] = [
			{
				cost: amounts.softwareAmount,
				description: t('placeholders.costSummary.serviceSoftware'),
			},
			{
				cost: amounts.bankAmount,
				description: t('placeholders.costSummary.serviceBank'),
			},
		];

		const ivaTaxText = t('general.withIva');

		const cost: CostSummaryCustomProps = {
			flow: 'subscription',
			commonProps: {
				name: planName,
				discountPrice: 0,
				monthsPromotion: promotionalCodeDuration || 0,
				mainPrice: amounts.finalMonthlyAmount || 0,
				footerInformation: ivaTaxText,
			},
			extendsProps: {
				hasPromotionalCode,
				summaryItemList: summaryList,
			},
		};

		return { cost };
	} else {
		const discountCost = solutionSelected?.monthlyAmountDiscount;
		const discountMonths = solutionSelected?.discountDurationMonths;

		const addStampDuty = t('general.addStampDuty');

		const cost: CostSummaryCustomProps = {
			flow: 'integratedSolution',
			commonProps: {
				name: solutionSelected?.name !== undefined ? solutionSelected.name : '',
				discountPrice: discountCost || 0,
				monthsPromotion: discountMonths || 0,
				mainPrice: solutionSelected?.monthlyAmount || 0,
				footerInformation: addStampDuty,
			},
			extendsProps: {
				solutionId: solutionSelected?.id as SolutionsType,
				hasDiscount: discountCost && discountCost > 0 ? true : false,
			},
		};

		return { cost };
	}
};
